// Apolo IDE — SquadCallLogDrawer (Squad Orchestrator E5).
//
// Trilha de AUDITORIA das chamadas LLM do orquestrador de um team turn: lê
// GET /api/hermes/runs/:id/calls (via squadCallLog.list) e lista
// stage/model/temp/top_p/prompt_hash/context_tokens em ordem de replay.
//
// NB sobre "replay": o ledger guarda só o `prompt_hash` (NUNCA o prompt cru —
// [SEC]), então não dá pra RE-EXECUTAR a chamada (não temos o prompt). O que
// o drawer entrega é a AUDITORIA verificável: provar que o structurer rodou em
// temp 0, ver os modelos/tamanhos por estágio, e o hash que torna o run
// auditável/comparável entre execuções.

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { squadCallLog } from '../../../hermes/infrastructure/squadCallLogClient.js';

const STAGE_LABEL = {
  reason: 'raciocínio',
  structure: 'estruturação',
  worker: 'operário',
  synthesis: 'síntese',
};

export default function SquadCallLogDrawer({ runId, onClose, client = squadCallLog }) {
  const [state, setState] = useState({ loading: true, calls: [], error: null });

  useEffect(() => {
    let alive = true;
    if (!runId) {
      setState({ loading: false, calls: [], error: null });
      return undefined;
    }
    setState({ loading: true, calls: [], error: null });
    client
      .list(runId)
      .then((calls) => alive && setState({ loading: false, calls: calls || [], error: null }))
      .catch(() => alive && setState({ loading: false, calls: [], error: 'Falha ao carregar' }));
    return () => {
      alive = false;
    };
  }, [runId, client]);

  const { loading, calls, error } = state;

  return (
    <div
      role="dialog"
      aria-label="Chamadas do orquestrador (auditoria)"
      className="mt-2 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 p-2 text-[11px]"
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="font-medium text-apolo-textSecondary">Chamadas do orquestrador</span>
        <span className="text-apolo-textTertiary">· trilha de auditoria (hash, não prompt)</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>

      {loading ? (
        <p className="text-apolo-textTertiary">Carregando…</p>
      ) : error ? (
        <p className="text-apolo-error">{error}</p>
      ) : calls.length === 0 ? (
        <p className="text-apolo-textTertiary">Nenhuma chamada registrada.</p>
      ) : (
        <ul className="space-y-1" aria-label="Lista de chamadas">
          {calls.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono">
              <span className="rounded bg-apolo-surface2 px-1 text-apolo-textSecondary">
                {STAGE_LABEL[c.stage] || c.stage}
              </span>
              <span className="text-apolo-textPrimary">{c.modelId}</span>
              {c.temperature != null ? (
                <span className="text-apolo-textTertiary">temp {c.temperature}</span>
              ) : null}
              {c.contextTokens != null ? (
                <span className="text-apolo-textTertiary">{c.contextTokens} tok</span>
              ) : null}
              <span className="text-apolo-textTertiary" title={c.promptHash}>
                #{String(c.promptHash || '').slice(0, 8)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
